import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import type { DialogProps } from '@mui/material';
import type { ReactNode } from 'react';
import { HANDOVER_TYPES, LEVEL_LABEL } from '@app/shared';
import type { AddressCheckDto, EnrollmentDto, UserRef } from '@app/shared';
import { EMPTY_TEXT, ErrorState, LoadingState } from '../../components/PageState';
import { formatCents, formatDateTime } from '../../lib/format';
import {
  useEnrollment,
  useEnrollmentAddressChecks,
  useEnrollmentApiLog,
  useEnrollmentLifecycle,
} from './api';
import { mailingAddress, paymentLabel, sourceLabel, stepLabel } from './labels';

/** Dialogs of the enrollment lists' "Option" menu. */

/** The row a dialog was opened for; null keeps it closed. */
export interface DialogTarget {
  id: string;
  enrollmentId: string;
}

function InfoDialog({
  open,
  title,
  onClose,
  maxWidth = 'sm',
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  maxWidth?: DialogProps['maxWidth'];
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Loading, error and empty states shared by the three dialogs. */
function Pending({
  query,
  empty,
}: {
  query: { error: unknown; isPending: boolean; refetch: () => unknown };
  empty: boolean;
}) {
  if (query.error)
    return (
      <Box sx={{ p: 2 }}>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Box>
    );
  if (query.isPending) return <LoadingState />;
  if (empty) return <Box sx={{ p: 2 }}>{EMPTY_TEXT}</Box>;
  return null;
}

const userText = (ref: UserRef | null | undefined): string =>
  ref ? `${ref.name} (${ref.loginId})` : '';

/** Everything known about an enrollment, as the Name / Value pairs of "View all details". */
function detailRows(e: EnrollmentDto): Array<[string, string]> {
  const a = e.applicant;
  return [
    ['Enrollment id', e.enrollmentId],
    ['Enrollment status', e.status],
    ['Enrollment step', stepLabel(e.step)],
    ['Incomplete reason', e.incompleteReason ?? ''],
    ['Source', sourceLabel(e.source)],
    ['Enrollment date', formatDateTime(e.createdAt)],
    ['Completed on', formatDateTime(e.completedAt)],
    ['First Name', a?.firstName ?? ''],
    ['Middle Initial', a?.middleInitial ?? ''],
    ['Last Name', a?.lastName ?? ''],
    ['Address 1', a?.addressLine1 ?? ''],
    ['Address 2', a?.addressLine2 ?? ''],
    ['City', a?.city ?? e.city],
    ['State', e.state],
    ['Zip', e.zip],
    ['Mailing address', mailingAddress(a)],
    ['Contact number', a?.phone ?? ''],
    ['Email', a?.email ?? ''],
    ['Plan name', e.plan?.name ?? ''],
    ['Plan price', formatCents(e.plan?.priceCents)],
    ['Handover Type', HANDOVER_TYPES.find((h) => h.key === e.handover?.type)?.label ?? ''],
    ['SIM', e.handover?.sim ?? ''],
    ['MDN', e.customer?.mdn ?? e.handover?.mdn ?? ''],
    ['Carrier', e.handover?.carrier.code ?? e.plan?.carrier.code ?? ''],
    ['Advance Billing Month', e.costs ? String(e.costs.advanceMonths) : ''],
    ['All Cost Excluding Taxes', formatCents(e.costs?.totalBeforeTaxCents)],
    ['Payment mode', e.payment ? paymentLabel(e.payment.method) : ''],
    ['Amount paid by customer', formatCents(e.payment?.amountCents)],
    ['Receipt Number', e.payment?.receiptNumber ?? ''],
    ['Customer ID', e.customer ? String(e.customer.customerId) : ''],
    [LEVEL_LABEL.master, userText(e.chain.master)],
    [LEVEL_LABEL.distributor, userText(e.chain.distributor)],
    [LEVEL_LABEL.retailer, userText(e.chain.retailer)],
    [LEVEL_LABEL.employee, userText(e.chain.employee)],
    ['Created by', userText(e.createdBy)],
  ];
}

export function DetailsDialog({
  target,
  onClose,
}: {
  target: DialogTarget | null;
  onClose: () => void;
}) {
  const enrollment = useEnrollment(target?.id);
  return (
    <InfoDialog open={Boolean(target)} title="View all details" onClose={onClose}>
      {enrollment.data ? (
        <Table aria-label="Enrollment details">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detailRows(enrollment.data).map(([name, value]) => (
              <TableRow key={name}>
                <TableCell component="th" scope="row" sx={{ width: 220 }}>
                  {name}
                </TableCell>
                <TableCell>{value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Pending query={enrollment} empty={false} />
      )}
    </InfoDialog>
  );
}

export function LifecycleDialog({
  target,
  onClose,
}: {
  target: DialogTarget | null;
  onClose: () => void;
}) {
  const events = useEnrollmentLifecycle(target?.id);
  return (
    <InfoDialog
      open={Boolean(target)}
      title={`Enrollment lifecycle${target ? `: ${target.enrollmentId}` : ''}`}
      onClose={onClose}
      maxWidth="md"
    >
      {events.data && events.data.length > 0 ? (
        <Table aria-label="Enrollment lifecycle">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}>#</TableCell>
              <TableCell>Event</TableCell>
              <TableCell>Date time</TableCell>
              <TableCell>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.data.map((event, i) => (
              <TableRow key={`${event.at}:${i}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{event.label}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(event.at)}</TableCell>
                <TableCell>{event.by?.loginId ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Pending query={events} empty={events.data?.length === 0} />
      )}
    </InfoDialog>
  );
}

const json = (value: unknown): string => JSON.stringify(value, null, 2);
const CODE = { m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' } as const;

/** The reference titles this dialog with the Enrollment ID; its columns are #, Api, Request, Response, Date time. */
export function ApiLogDialog({
  target,
  onClose,
}: {
  target: DialogTarget | null;
  onClose: () => void;
}) {
  const logs = useEnrollmentApiLog(target?.id);
  return (
    <InfoDialog
      open={Boolean(target)}
      title={`API log${target ? `: ${target.enrollmentId}` : ''}`}
      onClose={onClose}
      maxWidth="lg"
    >
      {logs.data && logs.data.length > 0 ? (
        <Table aria-label="API log">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}>#</TableCell>
              <TableCell>Api</TableCell>
              <TableCell>Request</TableCell>
              <TableCell>Response</TableCell>
              <TableCell>Date time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.data.map((log, i) => (
              <TableRow key={log.id} sx={{ verticalAlign: 'top' }}>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {log.api} {log.action}
                </TableCell>
                <TableCell>
                  <Box component="pre" sx={CODE}>
                    {json(log.request)}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box component="pre" sx={CODE}>
                    {json(log.response)}
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Pending query={logs} empty={logs.data?.length === 0} />
      )}
    </InfoDialog>
  );
}

/** "USPS address request response": the address checks of the order, # | Api | Request | Response | Date Time. */
export function AddressChecksDialog({
  target,
  onClose,
}: {
  target: DialogTarget | null;
  onClose: () => void;
}) {
  const checks = useEnrollmentAddressChecks(target?.id);
  const rows: AddressCheckDto[] = checks.data ?? [];
  return (
    <InfoDialog
      open={Boolean(target)}
      title="USPS address request response"
      onClose={onClose}
      maxWidth="lg"
    >
      {rows.length > 0 ? (
        <Table aria-label="USPS address request response">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}>#</TableCell>
              <TableCell>Api</TableCell>
              <TableCell>Request</TableCell>
              <TableCell>Response</TableCell>
              <TableCell>Date Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c, i) => (
              <TableRow key={c.id} sx={{ verticalAlign: 'top' }}>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.api}</TableCell>
                <TableCell>
                  <Box component="pre" sx={CODE}>
                    {c.request}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box component="pre" sx={CODE}>
                    {c.response}
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(c.at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Pending query={checks} empty={checks.data?.length === 0} />
      )}
    </InfoDialog>
  );
}
